import React, { useRef, useState, useEffect } from "react";

type Item = {
  id?: string;
  title: string;
  content: string;
};

type AccordionProps = {
  items: Item[];
  singleOpen?: boolean;
  durationClass?: string;
  easeClass?: string;
  className?: string;
};

export default function Accordion({
  items,
  singleOpen = false,
  durationClass = "duration-300",
  easeClass = "ease-out",
  className = "",
}: AccordionProps) {
  const [openIds, setOpenIds] = useState<number[]>([]);

  const toggle = (id: number) => {
    setOpenIds((prev) => {
      const isOpen = prev.includes(id);
      if (singleOpen) return isOpen ? [] : [id];
      return isOpen ? prev.filter((x) => x !== id) : [...prev, id];
    });
  };

  return (
    <div className={`w-full flex flex-col gap-6 ${className}`}>
      {items.map((item, idx) => (
        <AccordionRow
          key={idx}
          item={item}
          open={openIds.includes(idx)}
          onToggle={() => toggle(idx)}
          index={idx}
          total={items.length}
          durationClass={durationClass}
          easeClass={easeClass}
        />
      ))}
    </div>
  );
}

function AccordionRow({
  item,
  open,
  onToggle,
  index,
  total,
  durationClass,
  easeClass,
}: {
  item: Item;
  open: boolean;
  onToggle: () => void;
  index: number;
  total: number;
  durationClass: string;
  easeClass: string;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);

  useEffect(() => {
    if (!contentRef.current) return;
    if (open) {
      const el = contentRef.current;
      const full = el.scrollHeight;
      setHeight(full);
      const t = setTimeout(() => setHeight(el.scrollHeight), 0);
      return () => clearTimeout(t);
    } else {
      setHeight(0);
    }
  }, [open, item.content]);

  const onKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = (e) => {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      onToggle();
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const buttons = Array.from(
        (
          e.currentTarget.closest("[role='list']") ?? document
        ).querySelectorAll<HTMLButtonElement>(
          "button[role='button'][data-accordion-header='true']"
        )
      );
      const i = buttons.indexOf(e.currentTarget);
      const next =
        e.key === "ArrowDown"
          ? Math.min(i + 1, buttons.length - 1)
          : Math.max(i - 1, 0);
      buttons[next]?.focus();
    }
  };

  return (
    <div
      role="listitem"
      className="rounded-2xl border px-3 border-gray-300 py-3 lg:pr-12 shadow-blurShadow"
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={`section-${index}`}
        id={`header-${index}`}
        onClick={onToggle}
        onKeyDown={onKeyDown}
        role="button"
        data-accordion-header="true"
        className={`group flex w-full items-center justify-between gap-4 px-4 py-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500`}
      >
        <span className="text-[18px] font-medium text-textBlack3">
          {item.title}
        </span>
        <span
          className={`inline-flex h-6 w-6 items-center justify-center rounded-full transition-transform ${durationClass} ${easeClass} ${
            open ? "rotate-180" : "rotate-0"
          }`}
          aria-hidden
        >
          <svg viewBox="0 0 20 20" className="h-5 w-5">
            <path
              d="M5 7.5l5 5 5-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </span>
      </button>

      <div
        id={`section-${index}`}
        role="region"
        aria-labelledby={`header-${index}`}
        className={`overflow-hidden transition-[height,opacity] max-w-[80%] ${durationClass} ${easeClass} ${
          open ? "opacity-100" : "opacity-0"
        }`}
        style={{ height }}
      >
        <div
          ref={contentRef}
          className="px-4 pb-4 pt-0 text-[16px] text-gray-700"
        >
          {item.content}
        </div>
      </div>
    </div>
  );
}
